const CHAT_URL = '/api/chat';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

// ─── Types ──────────────────────────────────────────────────────────

export type ChatMessageRole = 'user' | 'assistant';
export type ChatMessageSource = 'text' | 'voice';

export interface ChatConversationAgent {
  id: number;
  name: string;
  color: string;
  has_avatar: boolean;
}

export interface ChatConversation {
  id: number;
  user_id: number;
  title: string;
  agent_id: number | null;
  agent?: ChatConversationAgent | null;
  created_at: string;
  updated_at: string;
}

export interface ChatAttachmentInfo {
  id: number;
  file_name: string;
  mime_type: string;
  file_size: number;
}

export interface ChatMessage {
  id: number;
  conversation_id: number;
  role: ChatMessageRole;
  content: string;
  source: ChatMessageSource;
  created_at: string;
  attachments?: ChatAttachmentInfo[];
}

export interface ConversationWithMessages extends ChatConversation {
  messages: ChatMessage[];
}

export interface SendMessageResponse {
  userMessage: ChatMessage;
  assistantMessage: ChatMessage;
}

// ─── Conversation API ────────────────────────────────────────────────

export async function createConversation(title?: string, agentId?: number): Promise<ChatConversation> {
  return fetchJson<ChatConversation>(`${CHAT_URL}/conversations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title: title ?? 'Neue Konversation', agentId }),
  });
}

export async function listConversations(): Promise<ChatConversation[]> {
  return fetchJson<ChatConversation[]>(`${CHAT_URL}/conversations`);
}

export async function getConversation(id: number): Promise<ConversationWithMessages> {
  return fetchJson<ConversationWithMessages>(`${CHAT_URL}/conversations/${id}`);
}

export async function sendMessage(
  conversationId: number,
  content: string,
): Promise<SendMessageResponse> {
  return fetchJson<SendMessageResponse>(
    `${CHAT_URL}/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    },
  );
}

export async function updateTitle(
  conversationId: number,
  title: string,
): Promise<ChatConversation> {
  return fetchJson<ChatConversation>(
    `${CHAT_URL}/conversations/${conversationId}/title`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    },
  );
}

export async function deleteConversation(conversationId: number): Promise<void> {
  const response = await fetch(`${CHAT_URL}/conversations/${conversationId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
}

export async function searchConversations(query: string): Promise<ChatConversation[]> {
  const params = new URLSearchParams({ q: query });
  return fetchJson<ChatConversation[]>(`${CHAT_URL}/conversations/search?${params}`);
}

// ─── Attachment API ─────────────────────────────────────────────────

export async function uploadAttachment(
  conversationId: number,
  file: File,
): Promise<ChatAttachmentInfo> {
  const response = await fetch(
    `${CHAT_URL}/conversations/${conversationId}/attachments`,
    {
      method: 'POST',
      headers: {
        'Content-Type': file.type,
        'X-File-Name': encodeURIComponent(file.name),
      },
      body: file,
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Upload failed: ${response.status}`);
  }
  return response.json() as Promise<ChatAttachmentInfo>;
}

export async function deleteAttachment(id: number): Promise<void> {
  const response = await fetch(`${CHAT_URL}/attachments/${id}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Delete failed: ${response.status}`);
  }
}

export function getAttachmentUrl(id: number): string {
  return `${CHAT_URL}/attachments/${id}`;
}

// Namespace export for convenience
export const chatApi = {
  createConversation,
  listConversations,
  getConversation,
  sendMessage,
  updateTitle,
  deleteConversation,
  searchConversations,
  uploadAttachment,
  deleteAttachment,
  getAttachmentUrl,
};
